import type { PrivilegeDefinition } from '@/core/be/security/SecurityTypes';
import type { FeatureModule } from '@/core/platform/features/FeatureModule';
import { IS_NEXT_EDGE_RUNTIME } from '@core/config/NextRuntime';
import { VaultEnvConfig } from './core/VaultEnvConfig';

export const VaultPrivilegeIds = {
  create: 'vault:create',
  delete: 'vault:delete',
  purge: 'vault:purge',
  settings: 'vault:settings',
  invite: 'vault:invite',
  read: 'vault:read',
  versionsRead: 'vault:versions:read',
  filesCreate: 'vault:files:create',
  filesEdit: 'vault:files:edit',
  filesDelete: 'vault:files:delete',
  filesMove: 'vault:files:move',
  foldersCreate: 'vault:folders:create',
  foldersEdit: 'vault:folders:edit',
  foldersDelete: 'vault:folders:delete',
  foldersMove: 'vault:folders:move',
} as const;

const VaultPrivileges: PrivilegeDefinition[] = [
  {
    id: VaultPrivilegeIds.create,
    scope: 'organization',
    feature: 'vaults',
    description: 'Create new vaults.',
  },
  {
    id: VaultPrivilegeIds.delete,
    scope: 'vault',
    feature: 'vaults',
    description: 'Soft-delete a vault (recoverable).',
  },
  {
    id: VaultPrivilegeIds.purge,
    scope: 'vault',
    feature: 'vaults',
    description: 'Permanently purge a deleted vault and its backups.',
  },
  {
    id: VaultPrivilegeIds.settings,
    scope: 'vault',
    feature: 'vaults',
    description: 'Manage vault settings and visibility.',
  },
  {
    id: VaultPrivilegeIds.invite,
    scope: 'vault',
    feature: 'vaults',
    description: 'Invite new members to a vault.',
  },
  {
    id: VaultPrivilegeIds.read,
    scope: 'vault',
    feature: 'vaults',
    description: 'Read vault content.',
  },
  {
    id: VaultPrivilegeIds.versionsRead,
    scope: 'vault',
    feature: 'vaults',
    description: 'View document version history.',
  },
  {
    id: VaultPrivilegeIds.filesCreate,
    scope: 'vault',
    feature: 'vaults',
    description: 'Create new files in vault.',
  },
  {
    id: VaultPrivilegeIds.filesEdit,
    scope: 'vault',
    feature: 'vaults',
    description: 'Edit existing files in vault.',
  },
  {
    id: VaultPrivilegeIds.filesDelete,
    scope: 'vault',
    feature: 'vaults',
    description: 'Delete files from vault.',
  },
  {
    id: VaultPrivilegeIds.filesMove,
    scope: 'vault',
    feature: 'vaults',
    description: 'Move or rename files in vault.',
  },
  {
    id: VaultPrivilegeIds.foldersCreate,
    scope: 'vault',
    feature: 'vaults',
    description: 'Create new folders in vault.',
  },
  {
    id: VaultPrivilegeIds.foldersEdit,
    scope: 'vault',
    feature: 'vaults',
    description: 'Rename folders in vault.',
  },
  {
    id: VaultPrivilegeIds.foldersDelete,
    scope: 'vault',
    feature: 'vaults',
    description: 'Delete folders from vault.',
  },
  {
    id: VaultPrivilegeIds.foldersMove,
    scope: 'vault',
    feature: 'vaults',
    description: 'Move folders in vault.',
  },
];

export class VaultFeatureModule {
  private static readonly definition: FeatureModule = {
    name: 'vaults',
    envSchema: VaultEnvConfig.getSchema(),
    privileges: VaultPrivileges,
    bootstrap: async () => {
      // Only run on server side (Node.js runtime)
      if (typeof window !== 'undefined' || IS_NEXT_EDGE_RUNTIME) {
        return;
      }

      // Dynamic import of Node.js-only bootstrap code
      // This keeps fs/promises out of the static module graph
      const { bootstrapVaultFeature } = await import('./VaultFeatureBootstrap.node');
      return bootstrapVaultFeature();
    },
  };

  private constructor() {}

  public static getDefinition(): FeatureModule {
    return VaultFeatureModule.definition;
  }
}
